import Joyride, {
  CallBackProps,
  STATUS,
  Step,
  TooltipRenderProps,
} from "react-joyride";
import { useState, FC, useEffect } from "react";
import useStore from "../../Store/Store";
import {
  Typography,
  Box,
  Dialog as MUIDialog,
  DialogContent,
} from "@mui/material";
import { theme } from "../../Themes/Themes";
import MessageDisplay from "../../utils/MessageDisplay";
import "../../Components/Dialog/Dialog.css";
import { centerGraph } from "../../utils/DiagramUtils";

interface GuidedWizardProps {
  steps: Step[];
  run?: boolean;
  isSimulationOnboarding?: boolean;
}

// Helper functions for creating and removing the custom highlight div
const createHighlightDiv = () => {
  // Remove existing div if it exists
  const existingDiv = document.getElementById("onboarding-diagram-highlight");
  if (existingDiv) {
    existingDiv.remove();
  }

  const div = document.createElement("div");
  div.id = "onboarding-diagram-highlight";
  div.style.position = "fixed";
  div.style.width = "400px";
  div.style.height = "400px";
  div.style.left = "calc(33vw - 200px)"; // Center horizontally at 30vw
  div.style.top = "calc(50vh - 200px)"; // Center vertically at 50vh
  div.style.backgroundColor = "transparent";
  div.style.border = "2px solid transparent";
  div.style.borderRadius = "8px";
  div.style.pointerEvents = "none";
  div.style.zIndex = "9999";
  document.body.appendChild(div);
};

const removeHighlightDiv = () => {
  const div = document.getElementById("onboarding-diagram-highlight");
  if (div) {
    div.remove();
  }
};

const CustomDialog: FC<
  TooltipRenderProps & {
    currentStepIndex: number;
    isSimulationOnboardingActive: boolean;
    setIsRunningSimulation: (isRunning: boolean) => void;
    setSelectedSimulationKey: (key: string) => void;
    joyrideHelpers: any;
    steps: Step[];
    onSkipClick: () => void;
  }
> = ({
  backProps,
  size,
  index,
  isLastStep,
  primaryProps,
  skipProps,
  step,
  tooltipProps,
  currentStepIndex,
  isSimulationOnboardingActive,
  setIsRunningSimulation,
  setSelectedSimulationKey,
  joyrideHelpers,
  steps,
  onSkipClick,
}: TooltipRenderProps & {
  currentStepIndex: number;
  isSimulationOnboardingActive: boolean;
  setIsRunningSimulation: (isRunning: boolean) => void;
  setSelectedSimulationKey: (key: string) => void;
  joyrideHelpers: any;
  steps: Step[];
  onSkipClick: () => void;
}) => {
  // Check if the next button should be disabled
  const shouldDisableNext =
    isSimulationOnboardingActive && (index === 2 || index === 4);

  // Custom back button handler
  const handleBackClick = async (event: React.MouseEvent<HTMLElement>) => {
    // Handle highlight div for diagram step (index 8)
    if (index === 8) {
      // Going back from diagram step - remove highlight div
      removeHighlightDiv();
    } else if (index === 9) {
      // Going back to diagram step - create highlight div
      createHighlightDiv();
    }

    // Get the step we're going back to (previous step)
    const previousStepIndex = index - 1;
    const previousStep =
      previousStepIndex >= 0 ? steps[previousStepIndex] : null;
    const previousStepTitle = previousStep?.title;

    // Execute custom logic based on the title of the step we're going back to
    if (previousStepTitle === "▶️ Start the Simulation") {
      // Going back from step 6 to step 5 - close joyride and restart with simulation state
      joyrideHelpers?.close();

      // Wait for the close to complete, then restart with new state sequentially
      setTimeout(() => {
        // Get the first simulation from the simulations list
        const storeState = useStore.getState();
        const simulationKeys = Object.keys(
          storeState.simulationsState.simulations
        );
        if (simulationKeys.length > 0) {
          const firstSimulationKey = simulationKeys[0];

          // Set the simulation state
          setSelectedSimulationKey(firstSimulationKey);

          // Wait a bit more, then restart the guided wizard
          setTimeout(() => {
            useStore.getState().setIsSimulationOnboardingActive(true);
            // Go to step 4 after a short delay to ensure everything is set up
            setTimeout(() => {
              joyrideHelpers?.go(4);
            }, 1000);
          }, 100);
        }
      }, 200);
    } else if (previousStepTitle === "🎯 Select a Use Case") {
      // Going back from step 4 to step 3 - clear selected simulation
      setSelectedSimulationKey("");
      setTimeout(() => {
        joyrideHelpers?.prev();
      }, 1000);
    } else {
      // Wait for 1 second before proceeding
      // Call the original back handler
      if (backProps?.onClick) {
        backProps.onClick(event);
      }
    }
  };

  // Custom next button handler
  const handleNextClick = async (event: React.MouseEvent<HTMLElement>) => {
    // Handle highlight div for diagram step (index 8)
    if (index === 7) {
      // Going from step 7 to diagram step - create highlight div
      createHighlightDiv();
    } else if (index === 8) {
      // Going from diagram step to next step - remove highlight div
      removeHighlightDiv();
    }

    const currentStepTitle = step?.title;

    // Execute custom logic based on the title of the current step we're leaving
    if (
      typeof currentStepTitle === "string" &&
      currentStepTitle.includes("Center")
    ) {
      // close the joyride
      joyrideHelpers?.close();

      // Wait for the close to complete, then restart with new state sequentially
      setTimeout(() => {
        // Get the first simulation from the simulations list
        const storeState = useStore.getState();
        const simulationKeys = Object.keys(
          storeState.simulationsState.simulations
        );
        if (simulationKeys.length > 0) {
          const firstSimulationKey = simulationKeys[0];

          // Set the simulation state
          setSelectedSimulationKey(firstSimulationKey);

          // Wait a bit more, then restart the guided wizard
          setTimeout(() => {
            useStore.getState().setIsSimulationOnboardingActive(true);
            // introduce some delay
            setTimeout(() => {
              joyrideHelpers?.go(11);
            }, 300);
          }, 1000);
        }
      }, 500);
    } else {
      // Call the original next handler immediately for other steps
      if (primaryProps?.onClick) {
        primaryProps.onClick(event);
      }
    }
  };

  // Custom complete button handler (completely reset and close tutorial)
  const handleCompleteClick = (event: React.MouseEvent<HTMLElement>) => {
    // Use the joyride helpers to close, which triggers the same callback as clicking overlay
    if (joyrideHelpers?.close) {
      joyrideHelpers.close();
      // center graph
      centerGraph();
    }
  };

  return (
    <Box
      {...tooltipProps}
      overflow="hidden"
      style={{
        backgroundColor: "whitesmoke",
        borderRadius: "30px",
        width: "35vw",
      }}
    >
      <br />
      <Typography
        textAlign={"center"}
        mx={3}
        my={0.8}
        fontSize="25px"
        fontFamily="Poppins-Light"
        color={theme.custom.darkerGrey}
        sx={{ paddingBottom: "15px", paddingTop: "15px" }}
      >
        {step?.title}
      </Typography>
      <Typography
        textAlign={"left"}
        mx={3}
        my={0.8}
        fontSize="15px"
        fontFamily="Poppins-Light"
        fontWeight="Light"
        color={theme.custom.darkerGrey}
        sx={{
          paddingBottom: "15px",
          paddingLeft: "15px",
          paddingRight: "15px",
        }}
      >
        <MessageDisplay message={step?.content.toString()} />
      </Typography>
      <div
        className="DialogButtonGroupHorizontal"
        style={{ paddingBottom: "10px" }}
      >
        {!isLastStep && (
          <div
            className="Button DialogButton"
            style={{ marginRight: "30px" }}
            onClick={onSkipClick}
          >
            <Typography
              mx={0.2}
              my={0.8}
              fontSize="15px"
              fontFamily="Poppins-Light"
              color="#ffaea6"
            >
              Skip
            </Typography>
          </div>
        )}
        {index > 0 && !isLastStep && (
          <div
            className="Button DialogButton"
            style={{ marginLeft: "30px" }}
            onClick={handleBackClick}
          >
            <Typography
              mx={0.2}
              my={0.8}
              fontSize="15px"
              fontFamily="Poppins-Light"
              color="#ffaea6"
            >
              Back
            </Typography>
          </div>
        )}
        <div
          className="Button DialogButton"
          style={{
            marginLeft: "30px",
            opacity: shouldDisableNext ? 0.5 : 1,
            cursor: shouldDisableNext ? "not-allowed" : "pointer",
          }}
          onClick={
            shouldDisableNext
              ? undefined
              : isLastStep
              ? handleCompleteClick
              : handleNextClick
          }
        >
          <Typography
            mx={0.2}
            my={0.8}
            fontSize="15px"
            fontFamily="Poppins-Light"
            fontWeight="Normal"
            color={shouldDisableNext ? "#ccc" : "#ffaea6"}
          >
            {isLastStep ? "Complete" : `Next (${index + 1}/${size})`}
          </Typography>
        </div>
      </div>
    </Box>
  );
};

// Skip Confirmation Dialog Component
const SkipConfirmationDialog: FC<{
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
}> = ({ open, onClose, onConfirm }) => {
  return (
    <MUIDialog
      open={open}
      onClose={onClose}
      sx={{
        zIndex: 10000300, // Much higher than joyride zIndex (10000100)
        "& .MuiPaper-root": {
          display: "flex",
          flexDirection: "row",
          justifyContent: "center",
          borderRadius: "30px",
          width: "40vw",
          zIndex: 10000301,
        },
        "& .MuiBackdrop-root": {
          zIndex: 10000299,
        },
      }}
    >
      <DialogContent className="DialogContent">
        <br />
        <Typography
          textAlign={"center"}
          mx={3}
          my={0.8}
          fontSize="18px"
          fontWeight="Light"
          color={theme.custom.darkerGrey}
          sx={{ paddingBottom: "10px" }}
        >
          We recommend you go through the entire tutorial as CodeCanvas is a new
          concept!
        </Typography>
        <div
          className="DialogButtonGroupHorizontal"
          style={{ paddingBottom: "10px" }}
        >
          <div
            className="Button DialogButton"
            style={{ marginRight: "30px" }}
            onClick={onClose}
          >
            <Typography
              mx={0.2}
              my={0.8}
              fontSize="15px"
              fontWeight="Normal"
              color="#FFAEA6"
            >
              Go Back
            </Typography>
          </div>
          <div
            className="Button DialogButton"
            style={{ marginLeft: "30px" }}
            onClick={onConfirm}
          >
            <Typography
              mx={0.2}
              my={0.8}
              fontSize="15px"
              fontWeight="Normal"
              color="#FFAEA6"
            >
              Continue Skip
            </Typography>
          </div>
        </div>
      </DialogContent>
    </MUIDialog>
  );
};

const GuidedWizard: FC<GuidedWizardProps> = ({
  steps,
  run,
  isSimulationOnboarding = false,
}) => {
  const {
    start,
    setStart,
    simulations,
    setSelectedSimulationKey,
    selectedSimulationKey,
    firstAISimulationName,
    setIsRunningSimulation,
    isRunningSimulation,
    isSimulationOnboardingActive,
  } = useStore((state) => ({
    start: state.startDemo,
    setStart: state.setStartDemo,
    simulations: state.simulationsState.simulations,
    setSelectedSimulationKey: state.setSelectedSimulationKey,
    selectedSimulationKey: state.simulationsState.selectedSimulationKey,
    firstAISimulationName: state.firstAISimulationName,
    setIsRunningSimulation: state.setIsRunningSimulation,
    isRunningSimulation: state.isRunningSimulation,
    isSimulationOnboardingActive: state.isSimulationOnboardingActive,
  }));

  // Track when tutorial should restart to force reset
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  // helpers: close, go(index), info, next, open, prev, reset, skip
  const [joyrideHelpers, setJoyrideHelpers] = useState<any>(null);
  // Skip confirmation dialog state
  const [showSkipConfirmationDialog, setShowSkipConfirmationDialog] =
    useState(false);

  // Modify steps to disable beacon on the first step so tutorial starts immediately
  const [data] = useState(() => {
    const modifiedSteps = [...steps];
    if (modifiedSteps.length > 0) {
      modifiedSteps[0] = {
        ...modifiedSteps[0],
        disableBeacon: true,
      };
    }
    return modifiedSteps;
  });

  // Use custom run prop if provided, otherwise fall back to default start state
  const shouldRun = run !== undefined ? run : start;

  // Handle skip button click - show confirmation dialog first
  const handleSkipClick = () => {
    setShowSkipConfirmationDialog(true);
  };

  // Handle skip confirmation
  const handleSkipConfirm = () => {
    setShowSkipConfirmationDialog(false);

    // Clean up highlight div when tutorial is skipped
    removeHighlightDiv();

    // Execute the skip logic with sequential state updates to avoid race conditions
    setSelectedSimulationKey("");
    setIsRunningSimulation(false);
    useStore.getState().setIsSimulationOnboardingActive(false);

    // Use a longer timeout and wait for multiple render cycles to ensure state is stable
    setTimeout(() => {
      // Double-check that joyride helpers are available and force a state update
      if (joyrideHelpers && data.length > 0) {
        // Add another small delay to ensure all state changes have propagated
        setTimeout(() => {
          try {
            joyrideHelpers.go(data.length - 1);
          } catch (error) {
            console.warn("Failed to navigate to last step:", error);
            // Fallback: try to close the tutorial gracefully
            joyrideHelpers.close();
          }
        }, 500);
      }
    }, 1500);
  };

  // Handle skip dialog close (go back)
  const handleSkipDialogClose = () => {
    setShowSkipConfirmationDialog(false);
  };

  const handleJoyrideCallback = (data: CallBackProps) => {
    const { status, index, action, type } = data;

    if (action === "close") {
      // Clean up highlight div when tutorial is closed
      removeHighlightDiv();
      setCurrentStepIndex(0);
      // joyrideHelpers?.go(0);
      setStart(false);
      setIsRunningSimulation(false);
      setSelectedSimulationKey("");
      useStore.getState().setIsSimulationOnboardingActive(false);
      useStore.getState().setShouldShowSimulationOnboarding(false);
      joyrideHelpers?.reset();
      return;
    }

    // Update current step index based on callback
    if (typeof index === "number") {
      setCurrentStepIndex(index);
    }
  };

  const helpers = (h: any) => {
    setJoyrideHelpers(h);
  };

  // useEffect if currentStepIndex is 2 and selectedSimulationKey is set, then use the props returned from helpers to call the next() function
  useEffect(() => {
    if (
      isSimulationOnboardingActive &&
      joyrideHelpers &&
      currentStepIndex === 2 &&
      selectedSimulationKey
    ) {
      joyrideHelpers.next();
    }
  }, [
    isSimulationOnboardingActive,
    currentStepIndex,
    selectedSimulationKey,
    joyrideHelpers,
  ]);

  // useEffect if currentStepIndex is 2 and selectedSimulationKey is set, then use the props returned from helpers to call the next() function
  useEffect(() => {
    if (
      isSimulationOnboardingActive &&
      joyrideHelpers &&
      currentStepIndex === 4 &&
      selectedSimulationKey &&
      isRunningSimulation
    ) {
      joyrideHelpers.next();
    }
  }, [
    isSimulationOnboardingActive,
    currentStepIndex,
    selectedSimulationKey,
    isRunningSimulation,
    joyrideHelpers,
  ]);

  return (
    <>
      <Joyride
        tooltipComponent={(props) => (
          <CustomDialog
            {...props}
            currentStepIndex={currentStepIndex}
            isSimulationOnboardingActive={isSimulationOnboardingActive}
            setIsRunningSimulation={setIsRunningSimulation}
            setSelectedSimulationKey={setSelectedSimulationKey}
            joyrideHelpers={joyrideHelpers}
            steps={data}
            onSkipClick={handleSkipClick}
          />
        )}
        steps={data}
        scrollToFirstStep={false}
        disableScrolling={true}
        continuous={true}
        run={shouldRun}
        callback={handleJoyrideCallback}
        getHelpers={helpers}
        spotlightClicks={true}
        showSkipButton={true}
        showProgress={false}
        disableOverlayClose={true}
        disableCloseOnEsc={false}
        hideBackButton={false}
        styles={{
          options: {
            arrowColor: "rgb(247, 247, 247)",
            backgroundColor: "rgb(247, 247, 247)",
            primaryColor: "#ffaea6",
            textColor: "#ffaea6",
            zIndex: 10000100,
          },
          overlay: {
            backgroundColor: "rgba(0, 0, 0, 0.3)",
          },
          spotlight: {
            backgroundColor: "rgba(255, 255, 255, 0.3)",
            border: "2px solid #ffaea6",
          },
        }}
      />

      {/* Skip Confirmation Dialog */}
      <SkipConfirmationDialog
        open={showSkipConfirmationDialog}
        onClose={handleSkipDialogClose}
        onConfirm={handleSkipConfirm}
      />
    </>
  );
};

export default GuidedWizard;
